import { get, find, uniq } from 'lodash';
import { getUnixTime, fromUnixTime } from 'date-fns';

import React from 'react';
import styled from 'styled-components';

import Button from '../../../Button';
import DatePicker from '../../../DatePicker';
import EditableSubject from '../../../EditableSubject';
import ManagerSelector from '../../Clients/ManagerSelector';
import Selector from '../../Common/Selector';
import TicketFilterByTag from '../TicketFilterByTag';
import CopyOnClick from '../../Common/CopyOnClick';
import { STATUSES } from '../TicketStatus/constants';
import TicketStatusSelector from '../TicketStatusSelector';
import { TYPES } from '../TicketType';
import TicketTypeSelector from '../TicketTypeSelector';
import TicketCloseReason from '../TicketCloseReason';
import TicketTemplateSelector from '../TicketTemplateSelector';
import Tooltip from '../../../Tooltip';
import Timer from '../Timer';
import TicketAssignCheckbox from '../TicketAssignCheckbox';
import { TICKET_TYPES } from '../../../../constants';
import TicketViewingManagersList from '../TicketViewingManagersList';
import Bubble from '../../../Bubble';
import { MailGroupType, TicketType } from 'types/types';
import {
  CloseReasonType,
  MenuSourceType,
  PlanReasonType,
  ReasonType,
  TypeOptionType,
} from '../types';
import { ValueOf } from '@utils/tsUtils';
import { TicketProps } from './types';
import { Copy } from '@icons/common';

const SOURCES = {
  MESSAGE: 'message',
  EMAIL: 'email',
};

const requiredFieldsTexts = {
  REASON: 'причина обращения',
  SUBJECT: 'тема',
  MAILGROUP: 'группа',
};

interface TicketState {
  closeReason?: string | null;
  closeReasonOption: CloseReasonType | null;
  isAssigned: boolean;
  isOpenRulesToolTip: boolean;
  readOnlyTags: boolean;
  mailGroup: string | null;
  mailGroupOption?: MailGroupType | null;
  manager: string | null;
  menuSource: string | null;
  menuSourceOption?: MenuSourceType | null;
  planReason: PlanReasonType | null;
  plannedAt: number | null;
  reason: string;
  reasonOption: ReasonType | null;
  status: ValueOf<typeof STATUSES>;
  subject: string;
  subtype: string;
  tags: string[];
  template?: string;
  templateOption?: any; // хз, что тут должно быть, по факту всегда undefined
  type: ValueOf<typeof TICKET_TYPES>;
  typeOption: string;
  typeSelectedOption?: TypeOptionType;
  notificationChannel?: string;
  notificationMessage?: string;
}

class Ticket extends React.PureComponent<TicketProps, TicketState> {
  static defaultProps = {
    className: '',
    readOnly: false,
    options: {
      reasons: [],
      mailGroups: [],
      managers: [],
      tags: [],
      types: [],
      closeReasons: [],
      orders: [],
      planReasons: [],
      menuSources: [],
      templates: [],
    },
    onCreate: Function.prototype,
    onClose: Function.prototype,
    onFieldChange: Function.prototype,
    onOrderSearch: Function.prototype,
  };

  static getDynamicStateFields(aProps, bProps) {
    const initialState = {
      subject: '',
      status: STATUSES.OPEN,
      type: TYPES.UNKNOWN,
      subtype: '',
      typeOption: '',
      mailGroup: null,
      reason: null,
      mailGroupOption: null,
      menuSourceOption: null,
      typeSelectedOption: null,
      templateOption: null,
      reasonOption: null,
      closeReason: null,
      closeReasonOption: null,
      notificationChannel: null,
      notificationMessage: null,
      tags: [],
      template: null,
      manager: null,
      isAssigned: false,
      planReason: null,
      plannedAt: null,
      menuSource: null,
    };

    const state = {} as TicketState;

    const hasChanges =
      JSON.stringify({
        ...aProps?.ticket,
        ...aProps?.options,
        tags: aProps?.ticket?.tags,
      }) !==
      JSON.stringify({
        ...bProps?.ticket,
        ...bProps?.options,
        tags: bProps?.ticket?.tags,
      });

    if (hasChanges) {
      const mailGroupsOptions = get(aProps, 'options.mailGroups');
      const menuSourcesOptions = get(aProps, 'options.menuSources');
      const typeOptions = get(aProps, 'options.types');
      const closeReasonOptions = get(aProps, 'options.closeReasons');
      const planReasons = get(aProps, 'options.planReasons', []);
      const plannedReasonId = get(aProps, 'ticket.plannedReasonId');
      const reasonOptions = get(aProps, 'options.reasons', []);
      const templateOptions = get(aProps, 'options.templates', []);

      state.type = get(aProps, 'ticket.type') || initialState.type;
      state.menuSource =
        get(aProps, 'ticket.menuSource') || initialState.menuSource;
      state.subtype = get(aProps, 'ticket.subtype') || initialState.subtype;
      state.mailGroup =
        get(aProps, 'ticket.mailGroup') || initialState.mailGroup;
      state.reason = get(aProps, 'ticket.reason') || initialState.reason;
      state.closeReason =
        get(aProps, 'ticket.closeReason') || initialState.closeReason;
      state.subject = get(aProps, 'ticket.subject') || initialState.subject;
      state.status = get(aProps, 'ticket.status.title') || initialState.status;
      state.tags = get(aProps, 'ticket.tags') || initialState.tags;
      state.manager = get(aProps, 'ticket.manager') || initialState.manager;
      state.isAssigned =
        get(aProps, 'ticket.isAssigned') || initialState.isAssigned;
      state.plannedAt =
        get(aProps, 'ticket.plannedAt') || initialState.plannedAt;
      state.typeSelectedOption =
        find(typeOptions, {
          type: state.type,
          subtype: state.subtype,
        }) || initialState.typeSelectedOption;
      state.mailGroupOption =
        find(mailGroupsOptions, {
          value: state.mailGroup,
        }) || initialState.mailGroupOption;
      state.menuSourceOption =
        find(menuSourcesOptions, {
          value: state.menuSource,
        }) || initialState.menuSourceOption;
      state.reasonOption =
        find(reasonOptions, {
          value: state.reason,
        }) || initialState.reasonOption;
      state.templateOption =
        find(templateOptions, {
          value: state.template,
        }) || initialState.templateOption;
      state.typeOption =
        get(state.typeSelectedOption, 'value') || initialState.typeOption;
      state.closeReasonOption =
        find(closeReasonOptions, {
          value: state.closeReason,
        }) || initialState.closeReasonOption;
      state.planReason =
        planReasons.find(({ value }) => value === plannedReasonId) ||
        initialState.planReason;
      state.readOnlyTags = false;
    }

    return state;
  }

  constructor(props) {
    super(props);

    const dynamicStateFields = Ticket.getDynamicStateFields(props, undefined);

    this.state = {
      ...dynamicStateFields,
      isOpenRulesToolTip: false,
      readOnlyTags: false,
    };

    this.handleCreate = this.handleCreate.bind(this);
    this.handleFieldSave = this.handleFieldSave.bind(this);
    this.handleMailGroupChange = this.handleMailGroupChange.bind(this);
    this.handleReasonChange = this.handleReasonChange.bind(this);
    this.handleManagerChange = this.handleManagerChange.bind(this);
    this.handleTicketReserve = this.handleTicketReserve.bind(this);
    this.handlePlannedTimeChange = this.handlePlannedTimeChange.bind(this);
    this.handleSourceChange = this.handleSourceChange.bind(this);
    this.handleClickStatus = this.handleClickStatus.bind(this);
    this.handleStatusChange = this.handleStatusChange.bind(this);
    this.handleSubjectChange = this.handleSubjectChange.bind(this);
    this.handleTemplateChange = this.handleTemplateChange.bind(this);
    this.handleTagChange = this.handleTagChange.bind(this);
    this.handleTypeChange = this.handleTypeChange.bind(this);
    this.toggleRulesToolTip = this.toggleRulesToolTip.bind(this);
    this.setInitialStatus = this.setInitialStatus.bind(this);
    this.renderPlanReasonSelect = this.renderPlanReasonSelect.bind(this);
    this.handlePlannedReasonChange = this.handlePlannedReasonChange.bind(this);
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props !== prevProps || this.state !== prevState) {
      this.setState(() => Ticket.getDynamicStateFields(this.props, prevProps));
    }
  }

  handleStatusChange({ detail }) {
    const { value: status } = detail;
    const { type, reasonOption } = this.state;

    const isTypeTourSelection = type === TYPES.TOUR_SELECTION;
    const isClosable = !!reasonOption;

    let localChange = STATUSES.PLANNED === status;

    if ((!isClosable || isTypeTourSelection) && status === STATUSES.CLOSED) {
      localChange = true;
    }

    this.setState(
      { status },
      () => !localChange && this.handleFieldSave({ status })
    );
  }

  handleManagerChange({ value: manager }) {
    const { ticket, onReserveTicket } = this.props;
    const { isAssigned } = this.state;

    const ticketId = get(ticket, 'id');

    this.setState({ manager }, () => this.handleFieldSave({ manager }));

    // снимаем закрепление тикета за менеджером, если произошло удаление ответственного
    if (!manager && isAssigned) {
      this.setState({ isAssigned: false });

      if (ticketId) {
        onReserveTicket(false);
      }
    }
  }

  handleTicketReserve(isAssigned) {
    const { ticket, onReserveTicket } = this.props;
    const ticketId = get(ticket, 'id');

    this.setState({ isAssigned });

    if (ticketId) {
      onReserveTicket(isAssigned);
    }
  }

  handleSubjectChange({ value: subject }) {
    this.setState({ subject }, () => this.handleFieldSave({ subject }));
  }

  handleTagChange({ target }) {
    const { ticket } = this.props;
    const { value } = target as { value: string[] };

    const tags = uniq<string>(value) || [];
    const ticketId = get(ticket, 'id');

    this.setState({ tags, readOnlyTags: !!ticketId }, () =>
      this.handleFieldSave({ tags })
    );
  }

  handlePlannedTimeChange(value) {
    const { planReason } = this.state;
    const date = new Date(value);

    this.setState({ plannedAt: getUnixTime(date) });

    this.handleFieldSave({
      plannedAt: date,
      planReasonId: planReason?.value,
      status: STATUSES.PLANNED,
    });
  }

  handleClickStatus(status) {
    if (status === STATUSES.PLANNED) {
      this.setState(() => ({ plannedAt: null }));
    }
  }

  handleTypeChange(value) {
    const { options } = this.props;
    const selectedOption = find(options.types, { value });
    const type = get(selectedOption, 'type') as TicketState['type'];
    const subtype = get(selectedOption, 'subtype') as TicketState['subtype'];
    const typeOption = get(
      selectedOption,
      'value'
    ) as TicketState['typeOption'];

    this.setState(
      {
        type,
        subtype,
        typeOption,
      },
      () => this.handleFieldSave({ type, subtype })
    );
  }

  handlePlannedReasonChange(reason) {
    this.setState(() => ({ planReason: reason }));
  }

  handleTemplateChange(template) {
    const { reason, mailGroup, notificationChannel, notificationMessage } =
      template;

    const updatedState = {
      reason,
      reasonOption:
        find(get(this.props, 'options.reasons'), {
          value: reason,
        }) || this.state.reasonOption,
      mailGroup,
      mailGroupOption:
        find(get(this.props, 'options.mailGroups'), {
          value: mailGroup,
        }) || this.state.mailGroupOption,
      notificationChannel,
      notificationMessage,
    };

    this.setState(updatedState, () =>
      this.handleFieldSave({
        reason,
        mailGroup,
        notificationChannel,
        notificationMessage,
      })
    );
  }

  handleMailGroupChange(mailGroupOption) {
    const { value: mailGroup } = mailGroupOption;
    this.setState(
      {
        mailGroupOption,
        mailGroup,
      },
      () => this.handleFieldSave({ mailGroup })
    );
  }

  handleSourceChange(menuSourceOption) {
    const { value: menuSource } = menuSourceOption;
    this.setState(
      {
        menuSourceOption,
      },
      () => this.handleFieldSave({ menuSource })
    );
  }

  handleReasonChange(reasonOption) {
    const { value: reason } = reasonOption;
    this.setState(
      {
        reasonOption,
        reason,
      },
      () => this.handleFieldSave({ reason })
    );
  }

  handleFieldSave(value) {
    const { ticket, onFieldChange } = this.props;
    const id = get(ticket, 'id');

    if (!id) {
      return;
    }

    onFieldChange(value);
  }

  handleCreate() {
    const { onCreate } = this.props;
    let plannedAt;

    const {
      reason,
      mailGroup,
      notificationChannel,
      notificationMessage,
      manager,
      isAssigned,
      plannedAt: plannedAtUnix,
      status,
      subject,
      subtype,
      tags,
      type,
    } = this.state;

    if (plannedAtUnix) {
      plannedAt = fromUnixTime(plannedAtUnix);
    }

    onCreate({
      reason,
      mailGroup: mailGroup as string,
      notificationChannel,
      notificationMessage,
      manager: manager as string,
      isAssigned,
      status,
      subject,
      tags,
      type,
      subtype,
      plannedAt,
    });
  }

  setInitialStatus() {
    const { ticket } = this.props;
    const status = get(ticket, 'status.title') as TicketState['status'];
    this.setState({ status });
  }

  toggleRulesToolTip() {
    this.setState(prevState => ({
      isOpenRulesToolTip: !prevState.isOpenRulesToolTip,
    }));
  }

  renderPlanReasonSelect() {
    const { planReason } = this.state;
    const { options } = this.props;
    const { planReasons } = options;

    return (
      <span
        onChange={event => {
          event.preventDefault();
          event.stopPropagation();
        }}
      >
        <StyledPlanReasonSelector
          isSearchable
          value={planReason}
          options={planReasons}
          onChange={this.handlePlannedReasonChange}
        />
      </span>
    );
  }

  render() {
    const {
      options,
      ticket,
      onClose,
      onOrderSearch,
      onReserveTicket,
      isCreateTicketLoading,
      isTicketReservationLoading,
      extraInfo,
      ...props
    } = this.props;

    const id = get(ticket, 'id');
    const source = get(ticket, 'source');
    const ruleIds = get(ticket, 'ruleIds');
    const closeReason = get(ticket, 'closeReason');
    const orderIds = get(ticket, 'orderIds');
    const priority = get(
      ticket,
      'status.priority'
    ) as TicketType['status']['priority'];
    const ltt = get(ticket, 'ltt');
    const sla = get(ticket, 'sla');
    const assignedAt = get(ticket, 'assignedAt') as string;
    const createdAt = get(ticket, 'createdAt') as number;
    const statusChangedAt = get(ticket, 'statusChangedAt') as number;

    const {
      reasons,
      closeReasons,
      managers,
      tags: tagOptions,
      types,
      menuSources,
      orders,
      mailGroups,
      templates,
      suggestedOrders,
    } = options;

    const {
      readOnlyTags,
      reason,
      reasonOption,
      isOpenRulesToolTip,
      mailGroup,
      mailGroupOption,
      manager,
      isAssigned,
      plannedAt,
      planReason,
      status,
      subject,
      notificationChannel,
      notificationMessage,
      templateOption,
      tags,
      menuSourceOption,
      type,
      typeOption,
      closeReasonOption,
    } = this.state;

    const isExistMandatoryFields = !!reason && !!subject && !!mailGroup;
    const isTypeTourSelection = type === TYPES.TOUR_SELECTION;
    const isStatusPlanned = status === STATUSES.PLANNED;

    const showRules = !isTypeTourSelection && ruleIds && ruleIds.length > 0;

    const isClosable = !!reasonOption;
    const showCloseReasons =
      (!isClosable || isTypeTourSelection) &&
      status === STATUSES.CLOSED &&
      !closeReason;

    const isLoadingTicketReservation = id ? isTicketReservationLoading : false;
    const isDisabledTicketReservation = !manager || isLoadingTicketReservation;

    // из assignedAt и statusChangedAt выбираем ту дату, которая позже (assignedAt - в формате ISO, statusChangedAt - в секундах)
    const getSlaSubtrahendTime = () => {
      const assignedAtDate = new Date(assignedAt);
      const statusChangedAtDate = new Date(statusChangedAt * 1000);
      return (
        assignedAtDate > statusChangedAtDate
          ? assignedAtDate
          : statusChangedAtDate
      ).toISOString();
    };

    const getLttSubtrahendTime = () => new Date(createdAt * 1000).toISOString();

    const createTicketButtonTooltipContent = () => (
      <>
        Не заполнены следующие поля:
        {!subject && <div>{`• ${requiredFieldsTexts.SUBJECT}`}</div>}
        {!mailGroup && <div>{`• ${requiredFieldsTexts.MAILGROUP}`}</div>}
        {!reason && <div>{`• ${requiredFieldsTexts.REASON}`}</div>}
      </>
    );

    return (
      <StyledTicket {...props}>
        <StyledHeader>
          {id && (
            <StyledTicketId>
              Тикет {id}
              <CopyOnClick
                valueForCopy={`${window.location.hostname}/tickets/${id}`}
                hintText="Скопировать ссылку на тикет"
              >
                <Copy width={16} />
              </CopyOnClick>
            </StyledTicketId>
          )}
          {showRules && (
            <StyledRules onClick={this.toggleRulesToolTip}>
              Правила
              <StyledRulesTooltip
                hideAction={this.toggleRulesToolTip}
                shown={isOpenRulesToolTip}
                width={200}
              >
                {ruleIds.map((ruleId, index) => (
                  <span>
                    {`${index + 1}) `}
                    <a
                      href={`https://level.travel/admin/crm_rules/${ruleId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {ruleId}
                    </a>
                  </span>
                ))}
              </StyledRulesTooltip>
            </StyledRules>
          )}
        </StyledHeader>
        <StyledPrefillHeader>
          <EditableSubject
            placeholder="Укажите тему тикета"
            value={subject}
            onSubmit={this.handleSubjectChange}
            isRequired={!id}
          />
          {!id && templates?.length > 0 && (
            <TicketTemplateSelector
              value={templateOption}
              options={templates}
              onChange={this.handleTemplateChange}
            />
          )}
        </StyledPrefillHeader>
        <StyledTicketStatusSelector
          type={type}
          value={status}
          priority={priority}
          time={(isStatusPlanned && plannedAt) || null}
          onChange={this.handleStatusChange}
          onClickStatus={this.handleClickStatus}
          orderIds={orderIds}
          closeReason={closeReasonOption}
          planReason={planReason}
        />
        {ticket && ltt && (
          <StyledLTTTime
            subtrahendTime={getLttSubtrahendTime()}
            seconds={ltt}
            type="LTT"
          />
        )}
        {ticket && sla && manager && (
          <StyledSLATime
            subtrahendTime={getSlaSubtrahendTime()}
            seconds={sla}
            type="SLA"
          />
        )}
        {showCloseReasons && (
          <TicketCloseReason
            orderIds={orderIds}
            reasonOptions={closeReasons}
            suggestedOrders={suggestedOrders}
            orderOptions={orders}
            onClose={this.setInitialStatus}
            onSubmit={onClose}
            onOrderSearch={onOrderSearch}
            isClosable={isClosable}
          />
        )}
        {isStatusPlanned && !plannedAt && (
          <StyledDatePicker
            showTimeSelect
            forceShown
            minDate={new Date()}
            showShortcuts
            submitLabel="Напомнить"
            onChange={this.handlePlannedTimeChange}
            suffix={this.renderPlanReasonSelect}
          />
        )}
        {types && (
          <StyledTypeSelector
            value={typeOption}
            options={types}
            onChange={this.handleTypeChange}
          />
        )}
        <StyledTagSelector
          key={JSON.stringify(tags)}
          isSearchable
          onChange={this.handleTagChange}
          tags={tagOptions}
          value={tags}
          readOnly={!!tags.length && readOnlyTags}
        />
        {managers && (
          <>
            <StyledManagerSelector
              onChange={this.handleManagerChange}
              managers={managers}
              value={manager}
              isClearable={status !== STATUSES.CLOSED}
            />
            <StyledTicketAssignCheckbox
              checked={isAssigned}
              disabled={isDisabledTicketReservation}
              isLoading={isLoadingTicketReservation}
              onReserveTicket={this.handleTicketReserve}
            />
          </>
        )}
        {SOURCES.MESSAGE === source && menuSources && menuSourceOption && (
          <StyledSourcesSelector
            isDisabled
            options={menuSources}
            placeholder="Источник обращения"
            value={menuSourceOption}
            onChange={this.handleSourceChange}
          />
        )}
        {mailGroups && (
          <StyledMailGroupSelector
            isSearchable
            onChange={this.handleMailGroupChange}
            options={mailGroups}
            placeholder="Выберите группу"
            value={mailGroupOption}
            isRequired={!id}
          />
        )}
        <StyledReasonSelector
          isSearchable
          onChange={this.handleReasonChange}
          options={reasons}
          placeholder="Причина обращения"
          value={reasonOption}
          isRequired={!id}
          filterOption={(option, rawInput) => {
            if (!option.data?.active) return false;
            if (!rawInput) return true;

            const label = (option.label ?? '').toLowerCase();
            return label.includes(rawInput.toLowerCase());
          }}
          noOptionsMessage={() => 'Требуется добавить себе скиллгруппу'}
        />
        {notificationChannel && (
          <StyledChannel>
            <b>Канал уведомления</b>: {notificationChannel}
          </StyledChannel>
        )}
        {notificationMessage && (
          <StyledChannel>
            <b>Сообщение уведомления</b>: {notificationMessage}
          </StyledChannel>
        )}
        {!!extraInfo?.viewingManagersIds && (
          <TicketViewingManagersList
            managers={managers}
            viewingManagersIds={extraInfo.viewingManagersIds}
          />
        )}
        {!id && (
          <StyledBubble
            text={createTicketButtonTooltipContent()}
            placement="top"
            disabled={isExistMandatoryFields}
          >
            <StyledButton
              data-tooltip-id="create-ticket-button"
              disabled={!isExistMandatoryFields || isCreateTicketLoading}
              onClick={this.handleCreate}
            >
              Создать тикет
            </StyledButton>
          </StyledBubble>
        )}
      </StyledTicket>
    );
  }
}

const StyledTicketId = styled.div`
  display: flex;
  gap: 4px;
`;

const StyledBubble = styled(Bubble)`
  & > div {
    width: 100%;
  }
`;

const StyledTicket = styled.div`
  width: 100%;
`;

const StyledHeader = styled.div`
  display: flex;
  justify-content: space-between;
`;

const StyledPrefillHeader = styled(StyledHeader)`
  align-items: flex-end;
`;

const StyledRules = styled.div`
  color: #919499;
  font-size: 12px;
  text-transform: uppercase;
  cursor: pointer;
`;

const StyledRulesTooltip = styled(Tooltip)`
  display: flex;
  font-size: 14px;
  flex-direction: column;
  top: calc(100% + 10px);
  bottom: auto;

  a {
    color: #007aff;
  }

  ::after {
    top: -9px;
    bottom: 0;
    left: calc(50% - 9px);
    border-top: none;
    border-bottom: 9px solid #fff;
    border-left: 9px solid transparent;
    border-right: 9px solid transparent;
  }
`;

const StyledTicketStatusSelector = styled(TicketStatusSelector)`
  width: 100%;
`;

const StyledChannel = styled.div`
  line-height: 23px;
  font-size: 14px;
  margin-left: 2px;
`;

const StyledTypeSelector = styled(TicketTypeSelector)`
  margin-top: 8px;
`;

const StyledManagerSelector = styled(ManagerSelector)`
  margin-top: 5px;
`;

const StyledTagSelector = styled(TicketFilterByTag)`
  margin-top: 10px;
  min-height: 30px;
  line-height: 30px;

  ${props =>
    props.readOnly &&
    `
    opacity: .5;
    pointer-events: none;
  `}
`;

const StyledMailGroupSelector = styled(Selector)`
  height: 30px;
  line-height: 30px;
`;

const StyledReasonSelector = styled(Selector)`
  height: 30px;
  line-height: 30px;
`;

const StyledSourcesSelector = styled(Selector)`
  margin-top: 5px;
  height: 30px;
  line-height: 30px;
`;

const StyledPlanReasonSelector = styled(Selector)`
  height: 30px;
  line-height: 30px;
  padding: 0 10px;
  margin-bottom: 10px;
`;

const StyledButton = styled(Button)`
  background: transparent;
  height: 36px;
  margin-top: 10px;
  width: 100%;
`;

const StyledDatePicker = styled(DatePicker)`
  flex: 1;
  height: 0;
  margin-left: -2px;
`;

const StyledTicketAssignCheckbox = styled(TicketAssignCheckbox)`
  margin-top: 5px;
`;

const StyledSLATime = styled(Timer)`
  margin-bottom: 5px;
`;

const StyledLTTTime = styled(Timer)`
  margin: 0 5px 5px 0;
`;

export default Ticket;
